import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { getService, relatedServices } from "@/data/services";
import { SITE } from "@/data/site";

function MetaCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-line bg-white p-5" data-testid={testid}>
      <p className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-gold-deep">{label}</p>
      <p className="mt-2 text-sm font-medium leading-relaxed text-navy-900">{value}</p>
    </div>
  );
}

function ListCard({ title, items }) {
  return (
    <div className="rounded-lg border border-line bg-mist p-6">
      <h3 className="text-base font-semibold text-navy-900">{title}</h3>
      <ul className="mt-4 flex flex-col gap-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-ink">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-sm bg-gold" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) {
    return (
      <section className="flex min-h-[70vh] flex-col items-center justify-center bg-white px-6 pt-32 text-center">
        <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-gold-deep">Not found</p>
        <h1 className="mt-4 text-3xl font-bold text-navy-900">That service doesn't exist.</h1>
        <Link to="/services" className="mt-8 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white" data-testid="service-notfound-back-link">
          <ArrowLeft className="h-4 w-4" /> All services
        </Link>
      </section>
    );
  }

  const related = relatedServices(slug);
  const Icon = service.icon;

  return (
    <>
      <Seo
        title={`${service.title} — SAIVU | Fixed fees, 50-50 payment terms`}
        description={`${service.tagline} ${service.short} Professional fees ${service.profFees}. Free 20-minute consultation.`}
        path={`/services/${service.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: service.intro,
          provider: { "@type": "Organization", name: "SAIVU — Saivu Consulting Pvt Ltd", url: "https://saivu.in" },
          areaServed: "IN",
        }}
      />

      <section className="bg-mist pt-28 pb-12 sm:pt-32" data-testid="service-detail-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <Reveal>
            <nav className="flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.12em] text-slate-600" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-navy-900" data-testid="service-breadcrumb-home">Home</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-navy-900" data-testid="service-breadcrumb-services">Services</Link>
              <span>/</span>
              <span className="font-semibold text-navy-900">{service.title}</span>
            </nav>
            <div className="mt-7 flex items-center gap-3">
              <span className="rounded-md border border-line bg-white p-2.5">
                <Icon className="h-5 w-5 text-navy-800" />
              </span>
              <span className="rounded-md border border-line bg-white px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">
                {service.category}
              </span>
            </div>
            <h1 className="mt-6 max-w-3xl text-3xl font-bold leading-[1.2] tracking-tight text-navy-900 sm:text-4xl">
              {service.tagline}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-lg">{service.intro}</p>
          </Reveal>

          <Reveal delay={0.12} className="mt-9 grid gap-4 sm:grid-cols-3">
            <MetaCard label="Timeline" value={service.timeline} testid="service-meta-timeline" />
            <MetaCard label="Government fees" value={service.govtFees} testid="service-meta-govt-fees" />
            <MetaCard label="Professional fees" value={service.profFees} testid="service-meta-prof-fees" />
          </Reveal>

          <Reveal delay={0.18}>
            <p className="mt-5 rounded-md border border-line bg-white px-4 py-3 text-sm leading-relaxed text-ink sm:text-[15px]" data-testid="service-fee-disclaimer">
              <span className="font-semibold text-navy-900">Note:</span> Fees shown are
              indicative ranges only. The final quotation may vary with the scope of the
              engagement —{" "}
              <span className="font-semibold text-navy-900">
                please consult our office and confirm the final fee/quotation before making
                any payment.
              </span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="text-xl font-semibold text-navy-900 sm:text-2xl">What you gain</h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {service.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 rounded-md border border-line bg-mist px-4 py-3 text-[15px] text-navy-900">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="mt-12">
              <h2 className="text-xl font-semibold text-navy-900 sm:text-2xl">How it works</h2>
              <ol className="mt-4 flex flex-col">
                {service.steps.map((step, i) => (
                  <li key={step} className="flex items-baseline gap-5 border-b border-line py-4 last:border-0">
                    <span className="font-mono text-sm font-semibold text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[15px] leading-relaxed text-ink">{step}</span>
                  </li>
                ))}
              </ol>
            </Reveal>

            <div className="mt-12 grid gap-5 sm:grid-cols-2">
              <Reveal>
                <ListCard title="Eligibility" items={service.eligibility} />
              </Reveal>
              <Reveal delay={0.07}>
                <ListCard title="Documents required" items={service.documents} />
              </Reveal>
            </div>

            <Reveal className="mt-12">
              <h2 className="text-xl font-semibold text-navy-900 sm:text-2xl">Deliverables</h2>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {service.deliverables.map((d) => (
                  <span key={d} className="rounded-md border border-navy-100 bg-navy-50 px-4 py-2 text-sm font-medium text-navy-900">
                    {d}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-12">
              <div className="rounded-lg border border-line border-l-4 border-l-gold bg-white p-6 shadow-card" data-testid="service-payment-note">
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-deep">Payment structure</p>
                <p className="mt-2.5 text-lg font-semibold text-navy-900">
                  50% before commencement · 50% after completion
                </p>
                <p className="mt-2 text-[15px] leading-relaxed text-ink">
                  Half of the professional fee is payable when work begins and the
                  remaining half only after successful completion and delivery.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal delay={0.1}>
                <div className="rounded-lg border border-line bg-mist p-7 sm:p-8" data-testid="service-lead-card">
                  <p className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-deep">Get started</p>
                  <h3 className="mt-3 text-2xl font-semibold text-navy-900">Request a callback.</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink">
                    A qualified CA will call you back — usually within a few working hours.
                  </p>
                  <div className="mt-6">
                    <LeadForm defaultService={service.title} id="service-lead" />
                  </div>
                  <a
                    href={SITE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-line bg-white px-6 py-3 text-sm font-medium text-navy-900 transition-colors hover:border-navy-900"
                    data-testid="service-whatsapp-button"
                  >
                    <MessageCircle className="h-4 w-4 text-emerald-600" /> Prefer WhatsApp? Talk now
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-white py-14" data-testid="related-services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <Reveal>
            <h2 className="text-xl font-semibold text-navy-900 sm:text-2xl">Often needed together</h2>
          </Reveal>
          <div className="mt-7 grid gap-5 md:grid-cols-3">
            {related.map((r, i) => {
              const RIcon = r.icon;
              return (
                <Reveal key={r.slug} delay={i * 0.06}>
                  <Link
                    to={`/services/${r.slug}`}
                    data-testid={`related-service-${r.slug}`}
                    className="group flex h-full flex-col rounded-lg border border-line bg-white p-6 transition-all duration-200 hover:border-navy-100 hover:shadow-lift"
                  >
                    <span className="rounded-md bg-mist p-2.5 w-fit">
                      <RIcon className="h-5 w-5 text-navy-800" />
                    </span>
                    <span className="mt-5 block text-base font-semibold text-navy-900">{r.title}</span>
                    <span className="mt-1.5 block flex-1 text-[15px] leading-relaxed text-ink">{r.short}</span>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-800">
                      Learn more <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
